/**
 * Optimized FontAwesome icon configuration
 * Only imports the icons actually used in the application, reduces bundle size significantly
 */

import { library, type IconDefinition } from '@fortawesome/fontawesome-svg-core';

// Regular icons
import {
  faBookOpenCover,
  faChevronLeft,
  faChevronRight,
  faCircleInfo,
  faCloud,
  faFileLines,
  faGlobe,
  faHeart,
  faHouse,
  faLink,
  faLock,
  faMagnifyingGlass,
  faNewspaper,
  faPen,
  faPlus,
  faPrint,
  faRightLeft,
  faRotateLeft,
  faShareFromSquare,
  faVideo,
  faXmark,
  faRocket,
  faImage,
  faRocketLaunch,
  faThoughtBubble,
  faHeadSideHeart,
  faSquareList,
  faCrown,
  faCircleCheck,
  faCalendarDays,
  faArrowDownArrowUp,
  faListUl,
  faCardsBlank,
  faPersonLimbsWide,
} from '@fortawesome/pro-regular-svg-icons';

// Solid icons
import {
  faArrowRight as faArrowRightSolid,
  faBarsFilter,
  faBook as faBookSolid,
  faBookOpenCover as faBookOpenCoverSolid,
  faCameraMovie as faCameraMovieSolid,
  faCaretDown as faCaretDownSolid,
  faCheck,
  faChevronDown as faChevronDownSolid,
  faChevronLeft as faChevronLeftSolid,
  faChevronRight as faChevronRightSolid,
  faChevronsDown,
  faChevronsLeft,
  faChevronsRight,
  faCircle as faCircleSolid,
  faCircleCheck as faCircleCheckSolid,
  faFileLines as faFileLinesSolid,
  faGamepad as faGamepadSolid,
  faGamepadModern as faGamepadModernSolid,
  faGraduationCap as faGraduationCapSolid,
  faHeadSideHeart as faHeadSideHeartSolid,
  faHeart as faHeartSolid,
  faInfoCircle as faInfoCircleSolid,
  faLock as faLockSolid,
  faMagnifyingGlass as faMagnifyingGlassSolid,
  faNewspaper as faNewspaperSolid,
  faPause,
  faPlay,
  faRectangleHistoryCirclePlus as faRectangleHistoryCirclePlusSolid,
  faRightLeft as faRightLeftSolid,
  faRocketLaunch as faRocketLaunchSolid,
  faRotateRight as faRotateRightSolid,
  faSeedling as faSeedlingSolid,
  faSquareList as faSquareListSolid,
  faThoughtBubble as faThoughtBubbleSolid,
  faUser as faUserSolid,
  faVideo as faVideoSolid,
  faVolumeHigh as faVolumeHighSolid,
  faXmark as faXmarkSolid,
  faTag as faTagSolid,
  faArrowRotateLeft as faArrowRotateLeftSolid,
  faGripDotsVertical,
} from '@fortawesome/pro-solid-svg-icons';

// Thin icons
import {
  faBookOpenReader as faBookOpenReaderThin,
  faCameraMovie as faCameraMovieThin,
  faBook as faBookThin,
  faChalkboardUser as faChalkboardUserThin,
  faCircleQuestion as faCircleQuestionThin,
  faCircleUser as faCircleUserThin,
  faFileLines as faFileLinesThin,
  faGamepad as faGamepadThin,
  faGamepadModern as faGamepadModernThin,
  faGear as faGearThin,
  faGrid as faGridThin,
  faMessageExclamation as faMessageExclamationThin,
  faMoon as faMoonThin,
  faNewspaper as faNewspaperThin,
  faSeedling as faSeedlingThin,
  faUsers as faUsersThin,
  faUserGroup as faUserGroupThin,
} from '@fortawesome/pro-thin-svg-icons';

// Light icons
import {
  faArrowRotateLeft as faArrowRotateLeftLight,
  faRectangleHistory as faRectangleHistoryLight,
  faUsers as faUsersLight,
  faVideo as faVideoLight,
  faBook as faBookLight,
  faBookOpen as faBookOpenLight,
  faFileLines as faFileLinesLight,
  faGraduationCap as faGraduationCapLight,
  faCommentsQuestion as faCommentsQuestionLight,
  faSquareList as faSquareListLight,
  faGamepad as faGamepadLight,
  faSubtitles as faSubtitlesLight,
  faNewspaper as faNewspaperLight,
} from '@fortawesome/pro-light-svg-icons';

// Duotone icons
import {
  faStreetView as faStreetViewDuotone,
  faKeySkeleton as faKeySkeletonDuotone,
  faSealQuestion as faSealQuestionDuotone,
  faEarthEurope as faEarthEuropeDuotone,
} from '@fortawesome/pro-duotone-svg-icons';

// Duotone light icons
import {
  faBoxBallot,
  faChalkboardUser as faChalkboardUserDuotoneLight,
  faImage as faImageDuotoneLight,
  faOctopus as faOctopusDuotoneLight,
  faFolderOpen as faFolderOpenDuotoneLight,
  faStreetView as faStreetViewDuotoneLight,
  faMoneyCheckPen as faMoneyCheckPenDuotoneLight,
  faHearts as faHeartsDuotoneLight,
} from '@fortawesome/duotone-light-svg-icons';

// Create arrays and spread them to avoid TypeScript issues
const regularIcons: IconDefinition[] = [
  faBookOpenCover,
  faChevronLeft,
  faChevronRight,
  faCircleInfo,
  faCloud,
  faGlobe,
  faFileLines,
  faHeart,
  faHouse,
  faLink,
  faLock,
  faMagnifyingGlass,
  faNewspaper,
  faPen,
  faPlus,
  faPrint,
  faRightLeft,
  faRotateLeft,
  faShareFromSquare,
  faVideo,
  faXmark,
  faRocket,
  faImage,
  faRocketLaunch,
  faThoughtBubble,
  faHeadSideHeart,
  faSquareList,
  faCrown,
  faCircleCheck,
  faCalendarDays,
  faArrowDownArrowUp,
  faListUl,
  faCardsBlank,
  faPersonLimbsWide,
];

const solidIcons: IconDefinition[] = [
  faGripDotsVertical,
  faArrowRightSolid,
  faBarsFilter,
  faBookSolid,
  faBookOpenCoverSolid,
  faCameraMovieSolid,
  faCaretDownSolid,
  faCheck,
  faChevronDownSolid,
  faChevronLeftSolid,
  faChevronRightSolid,
  faChevronsDown,
  faChevronsLeft,
  faChevronsRight,
  faCircleSolid,
  faCircleCheckSolid,
  faFileLinesSolid,
  faGamepadSolid,
  faGamepadModernSolid,
  faGraduationCapSolid,
  faHeadSideHeartSolid,
  faHeartSolid,
  faInfoCircleSolid,
  faLockSolid,
  faMagnifyingGlassSolid,
  faNewspaperSolid,
  faPause,
  faPlay,
  faRectangleHistoryCirclePlusSolid,
  faRightLeftSolid,
  faRocketLaunchSolid,
  faRotateRightSolid,
  faSeedlingSolid,
  faSquareListSolid,
  faThoughtBubbleSolid,
  faUserSolid,
  faVideoSolid,
  faVolumeHighSolid,
  faXmarkSolid,
  faTagSolid,
  faArrowRotateLeftSolid,
];

const thinIcons: IconDefinition[] = [
  faBookOpenReaderThin,
  faCameraMovieThin,
  faBookThin,
  faChalkboardUserThin,
  faCircleQuestionThin,
  faCircleUserThin,
  faFileLinesThin,
  faGamepadThin,
  faGamepadModernThin,
  faGearThin,
  faGridThin,
  faMessageExclamationThin,
  faMoonThin,
  faNewspaperThin,
  faSeedlingThin,
  faUsersThin,
  faUserGroupThin,
];

const lightIcons: IconDefinition[] = [
  faArrowRotateLeftLight,
  faRectangleHistoryLight,
  faUsersLight,
  faVideoLight,
  faBookLight,
  faBookOpenLight,
  faFileLinesLight,
  faGraduationCapLight,
  faCommentsQuestionLight,
  faSquareListLight,
  faGamepadLight,
  faSubtitlesLight,
  faNewspaperLight,
];

const duotoneIcons: IconDefinition[] = [
  faStreetViewDuotone,
  faSealQuestionDuotone,
  faKeySkeletonDuotone,
  faEarthEuropeDuotone,
];

const duotoneLightIcons: IconDefinition[] = [
  faBoxBallot,
  faChalkboardUserDuotoneLight,
  faImageDuotoneLight,
  faOctopusDuotoneLight,
  faFolderOpenDuotoneLight,
  faStreetViewDuotoneLight,
  faMoneyCheckPenDuotoneLight,
  faHeartsDuotoneLight,
];

// Add all icon arrays to the library
library.add(...regularIcons);
library.add(...solidIcons);
library.add(...thinIcons);
library.add(...lightIcons);
library.add(...duotoneIcons);
library.add(...duotoneLightIcons);
